import React, { useState } from 'react';
import { Plus, Trash2, CheckCircle, Clock, ChevronDown, ChevronRight } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import Modal from '../../components/common/Modal';
import Badge from '../../components/common/Badge';
import { formatDate, generateId } from '../../utils/helpers';

function HomeworkList() {
  const { homework, setHomework, classes, subjects, students, parents, notifications, setNotifications, hwCompletions, setHwCompletions } = useApp();
  const { currentUser } = useAuth();
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', classId: '', subjectId: '', dueDate: '', status: 'active' });
  const [expandedDates, setExpandedDates] = useState({});

  const today = new Date().toISOString().split('T')[0];

  // Resolve all of parent's children (siblings)
  const parentRecord = currentUser?.role === 'parent' ? parents?.find(p => p.id === currentUser?.profileId) : null;
  const childRecords = parentRecord ? students.filter(s => parentRecord.studentIds?.includes(s.id)) : [];
  // For backward compat (confirm button uses single child)
  const childRecord = childRecords[0] || null;

  const [selectedChildId, setSelectedChildId] = useState('all');

  // Filter homework visible to this user
  const visibleHomework = (() => {
    if (currentUser?.role === 'parent') {
      const classIds = selectedChildId === 'all'
        ? childRecords.map(c => c.classId)
        : [childRecords.find(c => c.id === selectedChildId)?.classId].filter(Boolean);
      return homework.filter(h => classIds.includes(h.classId));
    }
    if (currentUser?.role === 'student') {
      const student = students.find(s => s.id === currentUser?.profileId);
      return homework.filter(h => h.classId === student?.classId);
    }
    return homework;
  })();

  // Tag each homework with the matching child name (for parent multi-child view)
  const getChildForHw = (hw) => childRecords.find(c => c.classId === hw.classId);

  // Group by due date descending
  const grouped = visibleHomework.reduce((acc, hw) => {
    const key = hw.dueDate || hw.assignedDate || 'Unknown';
    if (!acc[key]) acc[key] = [];
    acc[key].push(hw);
    return acc;
  }, {});
  const sortedDates = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  const toggleDate = (date) => setExpandedDates(prev => ({ ...prev, [date]: !prev[date] }));

  // Has this parent confirmed completion of hw today (for the matching child)?
  const isConfirmed = (hw) => {
    if (currentUser?.role !== 'parent') return false;
    const child = getChildForHw(hw);
    return hwCompletions?.some(c => c.homeworkId === hw.id && c.parentId === currentUser?.id && c.studentId === child?.id && c.date === today);
  };

  const confirmCompletion = (hw) => {
    const child = getChildForHw(hw);
    const entry = {
      id: generateId('HWC'),
      homeworkId: hw.id,
      studentId: child?.id,
      parentId: currentUser?.id,
      date: today,
      confirmedAt: new Date().toISOString(),
    };
    setHwCompletions(prev => [...(prev || []), entry]);
  };

  const getCompletionCount = (hwId) => {
    const unique = new Set((hwCompletions || []).filter(c => c.homeworkId === hwId).map(c => c.studentId));
    return unique.size;
  };
  const getClassStudentCount = (classId) => students.filter(s => s.classId === classId && s.status === 'active').length;
  const getClass = (id) => { const c = classes.find(c => c.id === id); return c ? `${c.name}-${c.section}` : '-'; };
  const getSubject = (id) => subjects.find(s => s.id === id)?.name || '-';

  const save = () => {
    const hwId = generateId('HW');
    const newHw = { ...form, id: hwId, assignedDate: today, teacherId: currentUser?.profileId || 'STF-001' };
    setHomework(prev => [...prev, newHw]);
    const cls = classes.find(c => c.id === form.classId);
    const sub = subjects.find(s => s.id === form.subjectId);
    const ntf = {
      id: generateId('NTF'),
      type: 'homework',
      title: `New Homework: ${form.title}`,
      body: `${cls ? `${cls.name}-${cls.section}` : ''} | ${sub?.name || ''} | Due: ${form.dueDate}`,
      classId: form.classId,
      homeworkId: hwId,
      createdAt: new Date().toISOString(),
      targetRoles: ['parent'],
      readBy: [],
    };
    setNotifications(prev => [ntf, ...prev]);
    setModal(false);
    setForm({ title: '', description: '', classId: '', subjectId: '', dueDate: '', status: 'active' });
  };

  const remove = (id) => { if (window.confirm('Delete?')) setHomework(prev => prev.filter(h => h.id !== id)); };

  const isParent = currentUser?.role === 'parent';
  const isAdminOrTeacher = currentUser?.role === 'admin' || currentUser?.role === 'teacher';

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Homework</h1>
          <p className="text-gray-500 text-sm">{visibleHomework.length} assignments</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Sibling selector for parents with multiple children */}
          {isParent && childRecords.length > 1 && (
            <select
              value={selectedChildId}
              onChange={e => setSelectedChildId(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
            >
              <option value="all">All Children</option>
              {childRecords.map(c => (
                <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>
              ))}
            </select>
          )}
        {isAdminOrTeacher && (
          <button onClick={() => setModal(true)} className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-1 rounded-lg text-sm font-medium">
            <Plus size={16} /> Assign Homework
          </button>
        )}
        </div>
      </div>

      {/* Date-wise grouped list */}
      <div className="space-y-4">
        {sortedDates.map(date => {
          const isOpen = expandedDates[date] !== false; // default open
          const isToday = date === today;
          const isPast = date < today;
          return (
            <div key={date} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              {/* Date header */}
              <button
                onClick={() => toggleDate(date)}
                className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Clock size={16} className={isToday ? 'text-indigo-600' : isPast ? 'text-gray-400' : 'text-orange-500'} />
                  <span className={`font-semibold text-sm ${isToday ? 'text-indigo-700' : isPast ? 'text-gray-500' : 'text-orange-600'}`}>
                    {isToday ? 'Today — ' : ''}{formatDate(date)}
                    {isToday && <span className="ml-2 bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded-full">Due Today</span>}
                  </span>
                  <span className="">{grouped[date].length} assignment{grouped[date].length > 1 ? 's' : ''}</span>
                </div>
                {isOpen ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
              </button>

              {/* Homework cards under this date — grouped by subject */}
              {isOpen && (() => {
                // Group by subject
                const bySubject = grouped[date].reduce((acc, hw) => {
                  const subName = getSubject(hw.subjectId);
                  if (!acc[subName]) acc[subName] = [];
                  acc[subName].push(hw);
                  return acc;
                }, {});
                return (
                  <div className="border-t border-gray-100 divide-y divide-gray-50">
                    {Object.entries(bySubject).map(([subName, hws]) => (
                      <div key={subName} className="px-5 py-3">
                        {/* Subject header */}
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-bold text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-full">
                            {subName}
                          </span>
                          <span className="">{hws.length} task{hws.length > 1 ? 's' : ''}</span>
                        </div>
                        {/* Homework items under subject */}
                        <div className="space-y-2 pl-1">
                          {hws.map(hw => {
                            const confirmed = isConfirmed(hw);
                            const hwChild = isParent ? getChildForHw(hw) : null;
                            return (
                              <div key={hw.id} className="flex items-start justify-between gap-3 bg-gray-50 rounded-lg px-3 py-1">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                                    <h3 className="font-semibold text-sm">{hw.title}</h3>
                                    <Badge value={hw.status} />
                                    {isParent && childRecords.length > 1 && hwChild && (
                                      <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">
                                        {hwChild.firstName}
                                      </span>
                                    )}
                                  </div>
                                  <p className="mb-1">{hw.description}</p>
                                  <div className="flex items-center gap-3 flex-wrap">
                                    {!isParent && <span>Class: {getClass(hw.classId)}</span>}
                                    <span>Assigned: {formatDate(hw.assignedDate)}</span>
                                    {!isParent && (
                                      <span className="flex items-center gap-1 text-green-600 font-medium">
                                        <CheckCircle size={11} />
                                        {getCompletionCount(hw.id)}/{getClassStudentCount(hw.classId)} confirmed
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  {isParent && (
                                    <button
                                      onClick={() => !confirmed && confirmCompletion(hw)}
                                      disabled={confirmed}

                                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-semibold transition-colors ${
                                        confirmed
                                          ? 'bg-green-100 text-green-700 cursor-default'
                                          : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                                      }`}
                                    >
                                      <CheckCircle size={13} />
                                      {confirmed ? 'Completed' : 'Mark Done'}
                                    </button>
                                  )}
                                  {isAdminOrTeacher && (
                                    <button onClick={() => remove(hw.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-red-400">
                                      <Trash2 size={15} />
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          );
        })}
        {sortedDates.length === 0 && (
          <div className="bg-white rounded-xl p-10 text-center">No homework assigned</div>
        )}
      </div>

      {/* Assign modal (admin/teacher only) */}
      <Modal isOpen={modal} onClose={() => setModal(false)} title="Assign Homework">
        <div className="space-y-3">
          {[['Title', 'title', 'text'], ['Due Date', 'dueDate', 'date']].map(([label, key, type]) => (
            <div key={key}>
              <label className="block text-sm font-medium mb-1">{label}</label>
              <input type={type} value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
            </div>
          ))}
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea rows={3} value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 resize-none" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Class</label>
            <select value={form.classId || ''} onChange={e => setForm(f => ({ ...f, classId: e.target.value }))}
              className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
              <option value="">Select Class</option>
              {classes.map(c => <option key={c.id} value={c.id}>{c.name}-{c.section}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Subject</label>
            <select value={form.subjectId || ''} onChange={e => setForm(f => ({ ...f, subjectId: e.target.value }))}
              className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
              <option value="">Select Subject</option>
              {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
          <button onClick={() => setModal(false)} className="px-4 py-1 text-sm hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={save} className="px-4 py-1 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Assign</button>
        </div>
      </Modal>
    </div>
  );
}
export default HomeworkList;
